import {
  type ForwardedRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { useInterval } from '@elfsight/lib-utils';
import type { CountdownFinishHandler, TimerAPI, Unit } from '../types';

interface CountdownTimerProps {
  value: number;
  format: Unit[];
  isPaused: boolean;
  editMode: boolean;
  ref?: ForwardedRef<TimerAPI>;
  onCountdownFinish?: CountdownFinishHandler;
  onCountdownReset?: () => void;
}

export function useCountdownTimer({
  ref,
  value,
  format,
  editMode,
  isPaused,
  onCountdownFinish,
  onCountdownReset
}: CountdownTimerProps): Record<Unit, number> {
  const calledCallbackRef = useRef(false);
  const [time, setTime] = useState(Math.ceil(value / 1000));

  const shouldStopCounting = isPaused || time < 0;
  const interval = shouldStopCounting ? null : 1000;

  useInterval(() => setTime((prev) => prev - 1), interval);

  const reset = useCallback(() => {
    setTime(Math.ceil(value / 1000));

    if (value) {
      calledCallbackRef.current = false;
      onCountdownReset?.();
    }
  }, [value, onCountdownReset]);

  useLayoutEffect(() => {
    if (time > 0 || calledCallbackRef.current || isPaused) {
      return;
    }

    onCountdownFinish?.(value === 0);
    calledCallbackRef.current = true;
  }, [time, onCountdownFinish, isPaused, value]);

  useEffect(() => {
    if (isPaused && !editMode) {
      return;
    }

    reset();
  }, [reset, isPaused, editMode]);

  useImperativeHandle(ref, () => ({ reset }), [reset]);

  if (time < 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0
    };
  }

  const days = Math.floor(time / 86400);
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor(time / 60);
  const seconds = time;

  return {
    days,
    hours: format.includes('days') ? hours % 24 : hours,
    minutes: format.includes('hours') ? minutes % 60 : minutes,
    seconds: format.includes('minutes') ? seconds % 60 : seconds
  };
}
